import "reflect-metadata";
// PM5 T0 — must stay above every other app import. Renames the MUI v5+ static
// class names to the `FfV5` prefix so they cannot collide with the MUI v4 JSS
// `Mui*` classes still in the bundle. Several `@mui/material` modules bake the
// prefix in at module-evaluation time, so configuring it later is too late.
// See src/theme/classNamePrefix.ts.
import "./theme/classNamePrefix";
import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import App from "./components/App";
import "./index.css";
import * as serviceWorker from "./serviceWorker";
import { ENABLE_ROLLBAR } from "./env";
import { reportLegacyBrowser } from "./utils/legacyBrowser";
import { installMediaQueryListEventShim } from "./utils/mediaQueryListEventShim";
import { installStorageShim } from "./utils/storageShim";
import AppErrorFallback from "./components/common/AppErrorFallback";

// @ts-ignore
import * as rollbar from "@rollbar/react";

// Safari < 14 (the iOS 12.5 iPads in the #643 cohort) has no
// `MediaQueryList.addEventListener`, which MUI v5+ `useMediaQuery` calls with no
// fallback — a TypeError thrown in render, so every Footer-bearing route dies to
// the error boundary (#802). Must run before the first render below.
// See src/utils/mediaQueryListEventShim.ts.
installMediaQueryListEventShim();
// Browsers with web storage switched off (Android WebViews) expose
// `localStorage` as null; unguarded reads in mount effects then take the whole
// app to the error boundary (#871). See src/utils/storageShim.ts.
installStorageShim();
// Tag Rollbar occurrences by the stage we're actually running on, so web-test
// and local errors don't masquerade as production (#713). Mirrors the localhost
// check in serviceWorker.js. Any unlisted host (prod apex / www / aliases) falls
// through to "production" — a real prod error can never be hidden by this.
function rollbarEnvironment() {
  const host = window.location.hostname;
  if (host === "web-test.farmflix.tv") return "test";
  if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") {
    return "localhost";
  }
  return "production";
}

// Drop client errors from headless/bot user-agents before they reach Rollbar
// (#725). Synthetic traffic (crawlers, uptime monitors, scrapers) trips
// styled-components speedy-mode style injection (error #10) in ways real
// browsers never do, flooding Rollbar with noise no customer ever sees. UA-based
// so it filters ALL headless/bot client errors, not just #10 — intended: their
// JS errors aren't customer signal. Server-side breakage still surfaces elsewhere.
function isHeadlessOrBot() {
  return /Headless|bot|crawl|spider/i.test(navigator.userAgent || "");
}

const rollbarConfig = {
  accessToken: "37fc5604296c4fd8bf4eb050bee02935",
  environment: rollbarEnvironment(),
  enabled: ENABLE_ROLLBAR,
  checkIgnore: () => isHeadlessOrBot(),
};

const app = (
  <React.StrictMode>
    <rollbar.Provider config={rollbarConfig}>
      {/* fallbackUI is NOT optional (#774): with no fallback this boundary
          renders `null`, so a caught render error unmounts the tree and the
          customer is left looking at an empty <body> — a blank white page with
          no message and nothing to click, while Rollbar quietly records the
          occurrence. AppErrorFallback renders outside every provider by
          design; see its file header. */}
      <rollbar.ErrorBoundary fallbackUI={AppErrorFallback}>
        <App />
      </rollbar.ErrorBoundary>
    </rollbar.Provider>
  </React.StrictMode>
);

const rootElement = document.getElementById("root");
if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

// One-shot legacy-bundle telemetry: fires a Firebase `legacy_browser` event
// only when this browser lacks script.noModule (i.e. it loaded the es5
// plugin-legacy bundle). Measurement for the #643 keep/remove decision.
reportLegacyBrowser();
